/**
 * The shape contract for funnel_events.data
 *
 * funnel_events is the single event table, so every funnel has to agree on what
 * goes in the `data` JSON blob. Before this existed each funnel invented its own
 * conventions and prod ended up with the same key carrying different types
 * (checkout.data.is_guest was boolean on some rows and 0 on others,
 * checkout.data.listing_id number on some and string on others).
 *
 * The rules, enforced by normalizeFunnelEventData() rather than by convention:
 *
 * 1. Identity lives in columns, not in data. funnel_events already has
 *    session_id, user_id, is_guest, page_url, component, environment and
 *    timestamp as real columns. Repeating them inside data means two sources of
 *    truth that disagree (prod has data.user_id as a String next to a
 *    Nullable(UInt64) column). Those keys are dropped, and so is is_logged_in —
 *    it was a second name for the inverse of the is_guest column.
 *
 * 2. One name per fact. `step` and `event_type` both described a phase, so they
 *    are folded onto `event_step` rather than dropped — on an error event the
 *    phase is not the same thing as the event name.
 *
 * 3. Booleans are JSON booleans. Any key prefixed is_ or has_ given 0/1 or
 *    "true"/"false" is coerced, so `WHERE JSONExtractBool(data, 'is_guest')`
 *    behaves the same for every funnel.
 *
 * 4. *_id keys are numbers when they hold a numeric string. Ids arrive from
 *    router params and form state as strings; left alone they break numeric
 *    comparison in ClickHouse.
 *
 * 5. undefined and null keys are omitted. Emitters used to default absent fields
 *    to '' or 0, which is indistinguishable from a real empty string or a real
 *    zero (shipping_cost: 0 is meaningful, an absent shipping cost is not).
 *
 * 6. Errors use error_type / error_message / error_severity / error_fingerprint.
 *    The legacy loose `severity` key is mapped onto error_severity.
 *
 * Historical rows are left as they are — this governs everything written from
 * here on, for every funnel, with no per-funnel exceptions.
 */

/** Columns that funnel_events stores itself; repeating them in data is drift. */
const RESERVED_KEYS = new Set([
  'session_id',
  'user_id',
  'is_guest',
  'page_url',
  'component',
  'environment',
  'timestamp',
  'funnel',
  'event',
  'event_id',
  // is_logged_in was a second name for the inverse of the is_guest column;
  // prod had both, per funnel, which made "how many guests" unanswerable
  'is_logged_in',
]);

/**
 * Legacy keys folded onto canonical names. These are renamed rather than dropped:
 * `step` is not always a duplicate of the event column — on an error event it says
 * which phase failed (event='stripe_error', step='payment_attempted') — so the
 * fact is kept under one name instead of three.
 */
const LEGACY_KEY_RENAMES: Record<string, string> = {
  severity: 'error_severity',
  step: 'event_step',
  event_type: 'event_step',
};

function isBooleanKey(key: string): boolean {
  return key.startsWith('is_') || key.startsWith('has_');
}

function coerceBoolean(value: unknown): unknown {
  if (typeof value === 'boolean') return value;
  if (value === 0 || value === 1) return value === 1;
  if (value === 'true' || value === 'false') return value === 'true';
  if (value === '0' || value === '1') return value === '1';
  return value;
}

function coerceId(value: unknown): unknown {
  if (typeof value !== 'string' || value.trim() === '') return value;
  // Only plain integers; leave prefixed ids (pm_..., pi_..., uuid) alone
  if (!/^\d+$/.test(value)) return value;
  const n = Number(value);
  return Number.isSafeInteger(n) ? n : value;
}

/**
 * Apply the funnel_events data contract. Called by every write path
 * (client trackFunnelEvent and server logFunnelEvent/logFunnelEvents), so no
 * caller can opt out of it.
 */
export function normalizeFunnelEventData(
  data: Record<string, unknown> | undefined | null
): Record<string, unknown> {
  if (!data) return {};

  const out: Record<string, unknown> = {};

  for (const [rawKey, rawValue] of Object.entries(data)) {
    if (rawValue === undefined || rawValue === null) continue;

    const renamed = LEGACY_KEY_RENAMES[rawKey];
    // If the caller already set the canonical name, drop the legacy alias rather
    // than overwriting it — one fact, one key, and never a silent clobber.
    if (renamed && renamed in data) continue;
    const key = renamed ?? rawKey;
    if (RESERVED_KEYS.has(key)) continue;

    let value = rawValue;
    if (isBooleanKey(key)) value = coerceBoolean(value);
    else if (key.endsWith('_id')) value = coerceId(value);

    out[key] = value;
  }

  return out;
}
